import { api } from '@/components/Functional/lib/request'
import { logger } from '@/components/Functional/utils/logger'
import { RewardGetResponseSchema, RewardListResponseSchema, UserRewardTransactionGetResponseSchema } from '@/schemas/v2'
import type {
  MergedRewardData,
  RewardGetResponse,
  RewardListResponse,
  UserRewardStatusGetResponse,
  UserRewardStatusListResponse,
  UserRewardTransactionGetResponse,
} from '@/schemas/v2'
import { PhotoFrameResponseSchema } from '@/schemas/v2/photo-frame'
import type { PhotoFrameResponse } from '@/schemas/v2/photo-frame'

const BASE = process.env.NEXT_PUBLIC_API_URL ?? ''

type FetchOptions = { headers?: Record<string, string> }

export async function fetchRewards(options?: FetchOptions): Promise<RewardListResponse> {
  const url = `${BASE}/api/v2/rewards`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return RewardListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchReward(id: number, options?: FetchOptions): Promise<RewardGetResponse> {
  const url = `${BASE}/api/v2/rewards/${id}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return RewardGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postReward(
  id: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<UserRewardTransactionGetResponse> {
  const url = `${BASE}/api/v2/rewards/${id}`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return UserRewardTransactionGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/**
 * ゲスト向け特典一覧と認証済みユーザー向け特典一覧をマージする純粋関数。
 * Layer② に置くことで単体テスト可能にする。
 */
export function mergeRewardsWithUserData(
  rewardsResult: RewardListResponse,
  userRewardsResult: UserRewardStatusListResponse,
): { rewards: MergedRewardData[] } {
  const userRewardsMap = new Map(userRewardsResult.rewards.map((r) => [r.id, r]))
  return {
    rewards: rewardsResult.rewards.map((reward) => {
      const userReward = userRewardsMap.get(reward.id)
      return {
        ...reward,
        acquisitionStatus: userReward?.status ?? null,
        conditions: userReward?.conditions ?? [],
        // #3581: conditions と必ず同じ特典から取る（groupId の対応を保つため）
        conditionGroups: userReward?.conditionGroups ?? [],
      }
    }),
  }
}

/**
 * ゲスト向け特典詳細と認証済みユーザー向け特典詳細をマージする純粋関数。
 * Layer② に置くことで単体テスト可能にする。
 */
export function mergeRewardDetail(
  guestData: RewardGetResponse,
  authData: UserRewardStatusGetResponse | null,
): { reward: MergedRewardData | null } {
  const base = guestData.reward
  if (!base) return { reward: null }
  return {
    reward: {
      ...base,
      acquisitionStatus: authData?.reward?.status ?? null,
      conditions: authData?.reward?.conditions ?? [],
      // #3581: conditions と必ず同じレスポンスから取る（groupId の対応を保つため）
      conditionGroups: authData?.reward?.conditionGroups ?? [],
    },
  }
}

/**
 * POST /api/v2/user/reward-transactions/{id}/photo-frames
 * 取得済み特典の ARフォトフレーム情報を取得する（認証必須）。
 * レスポンス構造はスポットの photo-frames と同一。
 */
export async function postRewardTransactionPhotoFrames(
  transactionId: number,
  options?: FetchOptions,
): Promise<PhotoFrameResponse> {
  const url = `${BASE}/api/v2/user/reward-transactions/${transactionId}/photo-frames`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, {}, options)
    return PhotoFrameResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}
